import { Box, Button, CircularProgress, Grid, Typography, useTheme } from "@mui/material";
import { StatisticsCard } from "./StatisticsCard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BookIcon from "@mui/icons-material/Book";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useEffect, useState } from "react";
import { showErrorToast } from "../../../../../components/common/ShowToast";
import apiService from "../../../../../service/Api";
import { tokens } from "../../../../../theme";

interface Statistics {
  books_completed: number;
  books_delivered: number;
  books_in_progress: number;
  monthly_progress: number;
  total_books: number;
}

const defaultStats: Statistics = {
  books_completed: 0,
  books_delivered: 0,
  books_in_progress: 0,
  monthly_progress: 0,
  total_books: 0,
};

export const MemoryBookStats: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [booksStats, setBooksStats] = useState<Statistics>(defaultStats);
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);
  const [errorLoadingStats, setErrorLoadingStats] = useState<boolean>(false);

  useEffect(() => {
    fetchMemoryBooksStats();
  }, []);

  const fetchMemoryBooksStats = async () => {
    setIsLoadingStats(true);
    setErrorLoadingStats(false);

    try {
      const response = await apiService.get<{ data: Statistics }>(
        "/api/streams/stream/dashboard-statistics/"
      );
      setBooksStats(response.data.data);
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to load memory books statistics.");
      setErrorLoadingStats(true);
    } finally {
      setIsLoadingStats(false);
    }
  };

  if (isLoadingStats) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: colors.primary[900],
          width: "100%",
          height: 120,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <CircularProgress size={25} />
      </Box>
    );
  }

  if (errorLoadingStats) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: colors.primary[900],
          width: "100%",
          height: 120,
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Failed to load memory book statistics
          </Typography>
          <Button
            variant="contained"
            onClick={fetchMemoryBooksStats}
            sx={{ marginInline: "auto", display: "block" }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Grid container spacing={1} mb={3}>
      <StatisticsCard
        Icon={BookIcon}
        statisticFigure={booksStats.total_books}
        statisticTitle="Total books"
      />
      <StatisticsCard
        Icon={CheckCircleOutlineIcon}
        statisticFigure={booksStats.books_completed}
        statisticTitle="Books completed"
      />

      <StatisticsCard
        Icon={AccessTimeIcon}
        statisticFigure={booksStats.books_in_progress}
        statisticTitle="Books in Progress"
      />
      <StatisticsCard
        Icon={LocalShippingIcon}
        statisticFigure={booksStats.books_delivered}
        statisticTitle="Books Delivered"
      />
      <StatisticsCard
        Icon={CalendarTodayIcon}
        statisticFigure={booksStats?.monthly_progress}
        statisticTitle="Monthly Progress"
      />
    </Grid>
  );
};
